/**
 * Product page helpers: quantity-break cards and "copy promo code" button.
 */

/**
 * Lot cards are the quantity choice of the product form: the selected card (preselected one on
 * load) sets the quantity that the add-to-cart button sends, and the product price display shows
 * the lot total. The theme's quantity selector stays in the form (hidden by CSS) to carry the value.
 */
class BrandQuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onCardChange);
    this.quantityInput?.addEventListener('change', this.onQuantityChange);
    document.addEventListener('variant:update', this.onVariantUpdate);

    const checked = this.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) this.applyLot(checked);
    else this.syncFromQuantity();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onCardChange);
    this.quantityInput?.removeEventListener('change', this.onQuantityChange);
    document.removeEventListener('variant:update', this.onVariantUpdate);
  }

  get scope() {
    return this.closest('.product-details, product-information, .product-information') ?? document;
  }

  get quantityInput() {
    return this.scope.querySelector('product-form-component input[name="quantity"]');
  }

  onCardChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
    this.applyLot(radio);
  };

  onQuantityChange = () => this.syncFromQuantity();

  /** The theme re-renders the price on variant change: show the lot total again. */
  onVariantUpdate = () => {
    requestAnimationFrame(() => {
      const checked = this.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) this.updatePrice(checked);
    });
  };

  /** @param {HTMLInputElement} radio */
  applyLot(radio) {
    const input = this.quantityInput;
    if (input && input.value !== radio.value) {
      const selector = input.closest('quantity-selector-component');
      if (selector && typeof selector.setValue === 'function') {
        selector.setValue(radio.value);
        // Broadcasts QuantitySelectorUpdateEvent: the sticky bar shows the quantity too.
        selector.onQuantityChange?.();
        selector.updateButtonStates?.();
      } else {
        input.value = radio.value;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.updatePrice(radio);
  }

  /** @param {HTMLInputElement} radio */
  updatePrice(radio) {
    const container = this.scope.querySelector('product-price [ref="priceContainer"]');
    if (!container) return;
    const price = container.querySelector('.price bdi');
    if (price && radio.dataset.price) price.textContent = radio.dataset.price;
    const compareGroup = container.querySelector('.compare-at-price');
    const compare = compareGroup?.querySelector('bdi');
    if (!compareGroup || !compare) return;
    if (radio.dataset.compare) compare.textContent = radio.dataset.compare;
    compareGroup.style.display = radio.dataset.compare ? '' : 'none';
  }

  syncFromQuantity() {
    const value = this.quantityInput?.value;
    if (!value) return;
    const match = this.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
    if (match instanceof HTMLInputElement && !match.checked) {
      match.checked = true;
      this.updatePrice(match);
    }
  }
}

/** Copies a promo code to the clipboard and briefly confirms it. */
class BrandCopyCode extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.label = this.button?.textContent ?? '';
    this.status = this.querySelector('[role="status"]');
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  onClick = async () => {
    const code = this.dataset.code ?? '';
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    const label = this.label;
    const copied = this.dataset.copied || label;
    this.button.textContent = copied;
    if (this.status) this.status.textContent = copied;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.button.textContent = label;
      if (this.status) this.status.textContent = '';
    }, 2000);
  };
}

if (!customElements.get('brand-quantity-breaks')) customElements.define('brand-quantity-breaks', BrandQuantityBreaks);
if (!customElements.get('brand-copy-code')) customElements.define('brand-copy-code', BrandCopyCode);
